import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Building2, Users, Clock, Headphones, Car, 
  ChevronRight, Check, Send 
} from 'lucide-react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FleetRentalPage = () => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    vehicles: '',
    duration: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !formData.company) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/contact/fleet`, null, {
        params: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          message: `Vehicles: ${formData.vehicles}\nDuration: ${formData.duration}\n\n${formData.message}`
        }
      });
      toast.success('Your inquiry has been submitted. We will contact you shortly.');
      setFormData({ name: '', email: '', phone: '', company: '', vehicles: '', duration: '', message: '' });
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const benefits = [
    { 
      icon: Building2, 
      title: t('fleetRental.benefits.cost'),
      desc: t('fleetRental.benefits.costDesc')
    },
    { 
      icon: Clock, 
      title: t('fleetRental.benefits.flexibility'),
      desc: t('fleetRental.benefits.flexibilityDesc')
    },
    { 
      icon: Headphones, 
      title: t('fleetRental.benefits.support'),
      desc: t('fleetRental.benefits.supportDesc')
    },
    { 
      icon: Car, 
      title: t('fleetRental.benefits.variety'),
      desc: t('fleetRental.benefits.varietyDesc')
    },
  ];

  const fleetOptions = [
    { vehicles: '2-5', discount: '10%' },
    { vehicles: '6-10', discount: '15%' },
    { vehicles: '11-20', discount: '20%' },
    { vehicles: '20+', discount: '25%+' },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden" data-testid="fleet-hero">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-black/70 z-10" />
          <img
            src="https://images.unsplash.com/photo-1771284358704-ecb07b23bb55?w=1920&q=80"
            alt="Business fleet"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="section-container relative z-20">
          <div
            
            
            className="max-w-2xl"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-primary font-medium mb-4">
              {t('fleetRental.subtitle')}
            </p>
            <h1 className="font-serif text-5xl md:text-6xl font-light text-white mb-6">
              {t('fleetRental.title')}
            </h1>
            <p className="text-lg text-white/80 leading-relaxed">
              {t('fleetRental.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-spacing bg-[#F9FAFB]" data-testid="fleet-benefits">
        <div className="section-container">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl text-[#181B1F] font-light">
              {t('fleetRental.benefits.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                
                whileInView={{ opacity: 1, y: 0 }}
                
                viewport={{ once: true }}
                className="card-feature group"
              >
                <benefit.icon 
                  className="w-10 h-10 text-primary mb-4 group-hover:scale-110 transition-transform duration-300" 
                  strokeWidth={1.5} 
                />
                <h3 className="font-serif text-xl text-[#181B1F] mb-2">{benefit.title}</h3>
                <p className="text-gray-500 text-sm">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="section-spacing" data-testid="fleet-pricing">
        <div className="section-container">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl text-[#181B1F] font-light mb-4">
              {t('fleetRental.pricing.title', 'Fleet Discounts')}
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              {t('fleetRental.pricing.description', 'The more vehicles you rent, the more you save. Contact us for custom quotes on large fleets.')}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {fleetOptions.map((option, index) => (
              <div
                key={index}
                
                whileInView={{ opacity: 1, scale: 1 }}
                
                viewport={{ once: true }}
                className="glass-surface p-6 text-center"
              >
                <p className="text-gray-400 text-sm uppercase tracking-wider mb-2">
                  {option.vehicles} {t('fleetRental.pricing.vehicles', 'Vehicles')}
                </p>
                <p className="text-4xl text-primary font-light">
                  {option.discount}
                </p>
                <p className="text-gray-400 text-sm mt-1">{t('fleetRental.pricing.discount', 'discount')}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="section-spacing bg-[#F9FAFB]" data-testid="fleet-contact">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="font-serif text-4xl text-[#181B1F] font-light mb-6">
                {t('fleetRental.contact.title')}
              </h2>
              <p className="text-gray-500 mb-8">
                {t('fleetRental.contact.description', 'Fill out the form and our fleet team will get back to you within 24 hours with a customized quote.')}
              </p>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-primary" />
                  <span className="text-gray-600">{t('fleetRental.contact.benefit1', 'Dedicated account manager')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-primary" />
                  <span className="text-gray-600">{t('fleetRental.contact.benefit2', 'Flexible contract terms')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-primary" />
                  <span className="text-gray-600">{t('fleetRental.contact.benefit3', 'Priority maintenance support')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-primary" />
                  <span className="text-gray-600">{t('fleetRental.contact.benefit4', 'Replacement vehicle guarantee')}</span>
                </div>
              </div>
            </div>

            <motion.form
              
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              onSubmit={handleSubmit}
              className="glass-surface p-8 space-y-6"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-500">{t('fleetRental.contact.name', 'Name')} *</Label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="input-default"
                    placeholder={t('fleetRental.contact.namePlaceholder', 'Your name')}
                    data-testid="fleet-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">{t('fleetRental.contact.company')} *</Label>
                  <Input
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="input-default"
                    placeholder={t('fleetRental.contact.companyPlaceholder', 'Company name')}
                    data-testid="fleet-company"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-500">{t('fleetRental.contact.email', 'Email')} *</Label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="input-default"
                    placeholder="your@email.com"
                    data-testid="fleet-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">{t('fleetRental.contact.phone', 'Phone')} *</Label>
                  <Input
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="input-default"
                    placeholder="+41 22 123 45 67"
                    data-testid="fleet-phone"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-500">{t('fleetRental.contact.vehicles')}</Label>
                  <Select value={formData.vehicles} onValueChange={(v) => setFormData(prev => ({ ...prev, vehicles: v }))}>
                    <SelectTrigger className="input-default" data-testid="fleet-vehicles">
                      <SelectValue placeholder={t('fleetRental.contact.select', 'Select')} />
                    </SelectTrigger>
                    <SelectContent className="bg-white shadow-lg border-gray-200">
                      <SelectItem value="2-5" className="text-[#181B1F]">2-5 {t('fleetRental.pricing.vehicles', 'vehicles')}</SelectItem>
                      <SelectItem value="6-10" className="text-[#181B1F]">6-10 {t('fleetRental.pricing.vehicles', 'vehicles')}</SelectItem>
                      <SelectItem value="11-20" className="text-[#181B1F]">11-20 {t('fleetRental.pricing.vehicles', 'vehicles')}</SelectItem>
                      <SelectItem value="20+" className="text-[#181B1F]">20+ {t('fleetRental.pricing.vehicles', 'vehicles')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">{t('fleetRental.contact.duration')}</Label>
                  <Select value={formData.duration} onValueChange={(v) => setFormData(prev => ({ ...prev, duration: v }))}>
                    <SelectTrigger className="input-default" data-testid="fleet-duration">
                      <SelectValue placeholder={t('fleetRental.contact.select', 'Select')} />
                    </SelectTrigger>
                    <SelectContent className="bg-white shadow-lg border-gray-200">
                      <SelectItem value="1-3 months" className="text-[#181B1F]">1-3 {t('fleetRental.contact.months', 'months')}</SelectItem>
                      <SelectItem value="3-6 months" className="text-[#181B1F]">3-6 {t('fleetRental.contact.months', 'months')}</SelectItem>
                      <SelectItem value="6-12 months" className="text-[#181B1F]">6-12 {t('fleetRental.contact.months', 'months')}</SelectItem>
                      <SelectItem value="12+ months" className="text-[#181B1F]">12+ {t('fleetRental.contact.months', 'months')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-500">{t('fleetRental.contact.message')}</Label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="input-default min-h-[120px]"
                  placeholder={t('fleetRental.contact.messagePlaceholder', 'Tell us about your fleet needs...')}
                  data-testid="fleet-message"
                />
              </div>

              <Button 
                type="submit" 
                disabled={submitting}
                className="w-full btn-primary"
                data-testid="fleet-submit"
              >
                {submitting ? t('fleetRental.contact.submitting', 'Submitting...') : t('fleetRental.contact.submit')}
                <Send className="w-4 h-4 ml-2" />
              </Button>
            </motion.form>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default FleetRentalPage;
